import { Image } from 'expo-image';
import { StyleSheet, View } from 'react-native';

import { Text, tokens } from '@fhs/ui';
import {
  CART_ITEM_DISCOUNT_PRICE,
  CART_ITEM_FREE_PRICE,
  CART_ITEM_PRICE,
  ORIGINAL_CART_TOTAL,
} from '@fhs-legacy/frontend/src/utils/test-ids';

export function OriginalPriceDisplay({
  price,
  shouldShowStrikeThrough = false,
  subtotalID = false,
}: {
  price: number;
  shouldShowStrikeThrough?: boolean;
  subtotalID?: boolean;
}) {
  const formattedPrice = formatPrice(price);

  return (
    <Text.Ui
      testID={subtotalID ? ORIGINAL_CART_TOTAL : CART_ITEM_PRICE}
      size="md"
      style={shouldShowStrikeThrough && styles.crossedOutPrices}
      accessibilityRole="text"
      accessibilityLabel={
        shouldShowStrikeThrough
          ? `Original price ${formattedPrice}, discounted`
          : `Price ${formattedPrice}`
      }
    >
      ${formattedPrice}
    </Text.Ui>
  );
}

export function FreePriceDisplay({ showFree }: { showFree: boolean }) {
  return (
    <Text.Ui
      testID={CART_ITEM_FREE_PRICE}
      size="lg"
      weight="bold"
      style={styles.freePrice}
      accessibilityRole="text"
      accessibilityLabel={showFree ? 'Item is free' : ''}
    >
      {showFree && 'Free'}
    </Text.Ui>
  );
}

export function RewardPriceDisplay({ price }: { price: number }) {
  const formattedPrice = formatPrice(price);

  return (
    <Text.Ui
      testID={CART_ITEM_DISCOUNT_PRICE}
      size="lg"
      weight="bold"
      style={styles.freePrice}
      accessibilityRole="text"
      accessibilityLabel={`Reward price ${formattedPrice}`}
    >
      ${formattedPrice}
    </Text.Ui>
  );
}

export function OfferPriceDisplay({ price }: { price: number }) {
  const displayText = price === 0 ? 'Free' : `$${formatPrice(price)}`;
  const formattedPrice = formatPrice(price);

  return (
    <View
      style={styles.offerPrice}
      accessibilityRole="text"
      accessibilityLabel={price === 0 ? 'Item is free with offer' : `Offer price ${formattedPrice}`}
    >
      <Image
        source={require('./offer-icon.webp')}
        alt="Offer tag"
        style={styles.offerTagImage}
        accessibilityRole="image"
        accessibilityLabel="Offer applied"
        accessibilityElementsHidden
      />
      <Text.Ui testID={CART_ITEM_DISCOUNT_PRICE} size="lg" weight="bold" style={styles.freePrice}>
        {displayText}
      </Text.Ui>
    </View>
  );
}

const styles = StyleSheet.create({
  freePrice: {
    color: tokens.colors.$success,
  },
  crossedOutPrices: {
    color: tokens.colors.$disabledText,
    textDecorationLine: 'line-through',
    textDecorationStyle: 'solid',
  },
  offerPrice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
  },
  offerTagImage: { width: 16.67, height: 16.67 },
});

function formatPrice(priceInCents: number): string {
  return (priceInCents / 100).toFixed(2);
}
